// Ingredient/Herb glossary \u2014 every botanical used across Herbs We Love products
// Grouped by category. Each entry: name, latinName?, category, benefits, foundIn (product ids)

export const glossary = [
  // ---- Adaptogens ----
  {
    name: 'Ashwagandha',
    latin: 'Withania somnifera',
    category: 'Adaptogen',
    benefits: 'A classical adaptogenic root that helps the body respond to stress. Calms cortisol, eases emotional tension, and supports steady energy and mood.',
    foundIn: ['a-okay-elixir', 'boost-elixir', 'fireball-elixir', 'focus-capsules']
  },
  {
    name: 'Rhodiola',
    latin: 'Rhodiola rosea',
    category: 'Adaptogen',
    benefits: 'A high-altitude root prized for balancing stress and hormones while lifting mental stamina and focus.',
    foundIn: ['fireball-elixir', 'focus-capsules']
  },
  {
    name: 'Schisandra',
    latin: 'Schisandra chinensis',
    category: 'Adaptogen',
    benefits: 'A tart \u201cfive-flavor\u201d berry used in Traditional Chinese Medicine to build stamina, sharpen focus, and support the liver.',
    foundIn: ['fireball-elixir', 'respiratory-support-elixir']
  },
  {
    name: 'Maca',
    latin: 'Lepidium meyenii',
    category: 'Adaptogen',
    benefits: 'A Peruvian root that supports energy, endurance and vitality \u2014 traditionally used for hormonal balance and libido.',
    foundIn: ['fireball-elixir']
  },
  {
    name: 'Reishi',
    latin: 'Ganoderma lucidum',
    category: 'Medicinal Mushroom',
    benefits: 'The \u201cmushroom of immortality.\u201d Gently modulates the immune system, calms the nervous system, and supports deep restorative sleep.',
    foundIn: ['boost-elixir', 'somnific-capsules']
  },

  // ---- Calming & Sleep ----
  {
    name: 'Chamomile',
    latin: 'Matricaria chamomilla',
    category: 'Calming',
    benefits: 'A gentle flower that soothes the nervous system, aids digestion, and prepares the body for restful sleep.',
    foundIn: ['a-okay-elixir', 'sleep-syrup-w-cbn']
  },
  {
    name: 'Valerian Root',
    latin: 'Valeriana officinalis',
    category: 'Calming',
    benefits: 'A time-honored sleep herb that relaxes muscles and quiets a racing mind. Best in the evening.',
    foundIn: ['a-okay-elixir', 'sleep-syrup-w-cbn', 'somnific-capsules']
  },
  {
    name: 'Passionflower',
    latin: 'Passiflora incarnata',
    category: 'Calming',
    benefits: 'A dreamy climber that soothes anxious thoughts, eases tension and encourages a peaceful transition into sleep.',
    foundIn: ['sleep-syrup-w-cbn', 'somnific-capsules']
  },
  {
    name: 'Kava',
    latin: 'Piper methysticum',
    category: 'Calming',
    benefits: 'A traditional Pacific root used ceremonially to calm the mind and release nervous tension without dulling clarity.',
    foundIn: ['a-okay-elixir']
  },
  {
    name: 'Hops',
    latin: 'Humulus lupulus',
    category: 'Calming',
    benefits: 'Best known in brewing, but its calming, sleep-promoting compounds have been prized in herbal medicine for centuries.',
    foundIn: ['sleep-syrup-w-cbn', 'somnific-capsules']
  },
  {
    name: 'Magnolia Bark',
    latin: 'Magnolia officinalis',
    category: 'Calming',
    benefits: 'Contains honokiol \u2014 a gentle relaxant that supports mood, sleep quality and healthy cortisol rhythms.',
    foundIn: ['sleep-syrup-w-cbn']
  },
  {
    name: 'L-Tryptophan',
    category: 'Amino Acid',
    benefits: 'A precursor to serotonin and melatonin \u2014 supports mood regulation and healthy sleep cycles.',
    foundIn: ['sleep-syrup-w-cbn']
  },
  {
    name: 'Melatonin',
    category: 'Hormone',
    benefits: 'The body\u2019s natural sleep signal. A small dose helps re-align circadian rhythm and ease into deep sleep.',
    foundIn: ['somnific-capsules']
  },
  {
    name: 'GABA',
    category: 'Amino Acid',
    benefits: 'A calming neurotransmitter that dampens over-activity in the nervous system, supporting restful sleep and calm focus.',
    foundIn: ['somnific-capsules']
  },

  // ---- Immune & Respiratory ----
  {
    name: 'Elderberry',
    latin: 'Sambucus nigra',
    category: 'Immune',
    benefits: 'A dark, antioxidant-rich berry traditionally taken at the first sign of a cold to support the body\u2019s natural defenses.',
    foundIn: ['boost-elixir', 'respiratory-support-elixir']
  },
  {
    name: 'Echinacea',
    latin: 'Echinacea purpurea',
    category: 'Immune',
    benefits: 'A native prairie flower long used to prime the immune system when the seasons shift.',
    foundIn: ['boost-elixir']
  },
  {
    name: 'Mullein',
    latin: 'Verbascum thapsus',
    category: 'Respiratory',
    benefits: 'A tall, velvety plant traditionally used to soothe and support clear, comfortable breathing.',
    foundIn: ['respiratory-support-elixir']
  },
  {
    name: 'Plantain',
    latin: 'Plantago major',
    category: 'Respiratory',
    benefits: 'A humble but powerful leaf known for its soothing, mucous-membrane-loving properties.',
    foundIn: ['respiratory-support-elixir']
  },
  {
    name: 'NAC (N-Acetyl Cysteine)',
    category: 'Amino Acid',
    benefits: 'A form of the amino acid cysteine that supports antioxidant glutathione and respiratory wellness.',
    foundIn: ['respiratory-support-elixir']
  },
  {
    name: 'Cinnamon',
    latin: 'Cinnamomum verum',
    category: 'Warming',
    benefits: 'A warming spice that supports circulation, blood-sugar balance and gentle immune resilience.',
    foundIn: ['boost-elixir']
  },
  {
    name: 'Ginger',
    latin: 'Zingiber officinale',
    category: 'Warming',
    benefits: 'A fiery rhizome that eases nausea, supports digestion, warms the body and helps clear a stuffy day.',
    foundIn: ['boost-elixir', 'respiratory-support-elixir']
  },
  {
    name: 'Colloidal Silver',
    category: 'Immune',
    benefits: 'A traditional wellness ingredient believed to support immune resilience when used mindfully.',
    foundIn: ['boost-elixir']
  },
  {
    name: 'Vitamin C',
    category: 'Vitamin',
    benefits: 'An essential antioxidant that supports immune function, collagen synthesis and everyday resilience.',
    foundIn: ['boost-elixir']
  },

  // ---- Focus & Cognition ----
  {
    name: 'Bacopa',
    latin: 'Bacopa monnieri',
    category: 'Nootropic',
    benefits: 'An Ayurvedic herb honored for memory, learning and calm focus. Best taken consistently.',
    foundIn: ['focus-capsules']
  },
  {
    name: 'Gotu Kola',
    latin: 'Centella asiatica',
    category: 'Nootropic',
    benefits: 'Called \u201cthe herb of enlightenment\u201d \u2014 supports mental clarity, circulation and healthy connective tissue.',
    foundIn: ['focus-capsules']
  },
  {
    name: 'Guarana',
    latin: 'Paullinia cupana',
    category: 'Energy',
    benefits: 'A caffeine-rich Amazonian seed traditionally used for stamina, focus and endurance.',
    foundIn: ['fireball-elixir']
  },
  {
    name: 'Yohimbine',
    latin: 'Pausinystalia yohimbe',
    category: 'Circulation',
    benefits: 'A traditional bark extract used to support blood flow, stamina and performance.',
    foundIn: ['fireball-elixir']
  },

  // ---- CBD family ----
  {
    name: 'CBD Isolate',
    category: 'Cannabinoid',
    benefits: 'Pure cannabidiol extracted from hemp \u2014 THC-free. Interacts with the endocannabinoid system to support calm, comfort and balance.',
    foundIn: ['a-okay-elixir', 'boost-elixir', 'calming-pet-drops', 'sleep-syrup-w-cbn', 'focus-capsules', 'fireball-elixir']
  },
  {
    name: 'Nano CBD',
    category: 'Cannabinoid',
    benefits: 'CBD broken down into nano-sized particles for faster absorption and greater bio-availability. Same plant, gentler learning curve.',
    foundIn: ['a-okay-elixir', 'boost-elixir', 'fireball-elixir', 'sleep-syrup-w-cbn', 'somnific-capsules', 'respiratory-support-elixir', 'coconut-vanilla-sugar-body-scrub', 'lavender-eucalyptus-sugar-body-scrub', 'strawberry-delight-sugar-body-scrub', 'rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'CBN',
    category: 'Cannabinoid',
    benefits: 'A minor cannabinoid known for its sleep-supporting, deeply relaxing properties. Beautiful alongside CBD in evening rituals.',
    foundIn: ['sleep-syrup-w-cbn']
  },

  // ---- Skincare & Carrier Oils ----
  {
    name: 'Coconut Oil',
    latin: 'Cocos nucifera',
    category: 'Carrier Oil',
    benefits: 'Deeply moisturizing and antimicrobial. Softens skin and lips while carrying botanicals gently into the body.',
    foundIn: ['a-okay-elixir', 'coconut-vanilla-sugar-body-scrub', 'lavender-eucalyptus-sugar-body-scrub', 'strawberry-delight-sugar-body-scrub', 'cbd-lip-quench-collection', 'cherry-vanilla-lip-balm']
  },
  {
    name: 'MCT Oil',
    category: 'Carrier Oil',
    benefits: 'Medium-chain triglycerides from coconut \u2014 fast-absorbing and easy on the stomach. Great carrier for CBD.',
    foundIn: ['calming-pet-drops', 'sleep-syrup-w-cbn', 'rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Jojoba Oil',
    latin: 'Simmondsia chinensis',
    category: 'Carrier Oil',
    benefits: 'A liquid wax that mimics our own skin\u2019s sebum \u2014 balancing, non-greasy and beautifully absorbed.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Meadowfoam Oil',
    latin: 'Limnanthes alba',
    category: 'Carrier Oil',
    benefits: 'One of the most stable plant oils, rich in long-chain fatty acids that lock in moisture and give skin a silky finish.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Avocado Oil',
    latin: 'Persea americana',
    category: 'Carrier Oil',
    benefits: 'A lush, vitamin-rich oil that deeply nourishes skin and helps other botanicals penetrate more effectively.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Borage Oil',
    latin: 'Borago officinalis',
    category: 'Carrier Oil',
    benefits: 'Rich in GLA (gamma-linolenic acid) \u2014 loved for supporting soft, calm, resilient skin.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Shea Butter',
    latin: 'Vitellaria paradoxa',
    category: 'Skin Care',
    benefits: 'A rich, buttery emollient from the shea tree of West Africa. Deeply hydrating for lips and body.',
    foundIn: ['cbd-lip-quench-collection', 'cherry-vanilla-lip-balm']
  },
  {
    name: 'Beeswax',
    category: 'Skin Care',
    benefits: 'A natural protectant that seals in moisture while letting skin breathe. Gives lip balms their soft structure.',
    foundIn: ['cbd-lip-quench-collection', 'cherry-vanilla-lip-balm']
  },
  {
    name: 'Vitamin E',
    category: 'Vitamin',
    benefits: 'A skin-loving antioxidant that supports softness, radiance and long-lasting freshness of natural oils.',
    foundIn: ['coconut-vanilla-sugar-body-scrub', 'lavender-eucalyptus-sugar-body-scrub', 'strawberry-delight-sugar-body-scrub', 'cbd-lip-quench-collection', 'cherry-vanilla-lip-balm', 'relax-massage-oil']
  },
  {
    name: 'Hemp Seed Oil',
    latin: 'Cannabis sativa',
    category: 'Carrier Oil',
    benefits: 'A perfectly balanced omega-3 & omega-6 seed oil (no CBD) \u2014 deeply moisturizing for skin, scalp and lips.',
    foundIn: ['coconut-vanilla-sugar-body-scrub', 'lavender-eucalyptus-sugar-body-scrub', 'strawberry-delight-sugar-body-scrub', 'cbd-lip-quench-collection']
  },

  // ---- Essential Oils ----
  {
    name: 'Lavender Essential Oil',
    latin: 'Lavandula angustifolia',
    category: 'Essential Oil',
    benefits: 'The gentle giant of aromatherapy \u2014 calming, sleep-supporting, skin-soothing.',
    foundIn: ['lavender-eucalyptus-sugar-body-scrub', 'therapeutic-massage-oil']
  },
  {
    name: 'Eucalyptus Essential Oil',
    latin: 'Eucalyptus globulus',
    category: 'Essential Oil',
    benefits: 'A cool, camphorous oil that clears the senses, opens airways and lifts the spirit.',
    foundIn: ['lavender-eucalyptus-sugar-body-scrub', 'rejuvenating-massage-oil']
  },
  {
    name: 'Peppermint Essential Oil',
    latin: 'Mentha piperita',
    category: 'Essential Oil',
    benefits: 'Instantly cooling and energizing \u2014 supports circulation, focus and tired muscles.',
    foundIn: ['a-okay-elixir', 'rejuvenating-massage-oil', 'respiratory-support-elixir', 'cbd-lip-quench-collection']
  },
  {
    name: 'Frankincense Essential Oil',
    latin: 'Boswellia carterii',
    category: 'Essential Oil',
    benefits: 'A sacred resin used since antiquity for meditation, skin support and deep grounding.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Myrrh Essential Oil',
    latin: 'Commiphora myrrha',
    category: 'Essential Oil',
    benefits: 'A warm, earthy resin traditionally used for skin, soothing sore areas, and quiet reflection.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil', 'therapeutic-massage-oil']
  },
  {
    name: 'Sandalwood Essential Oil',
    latin: 'Santalum album',
    category: 'Essential Oil',
    benefits: 'A creamy, grounding wood oil beloved for meditation, deep relaxation and skin comfort.',
    foundIn: ['relax-massage-oil']
  },
  {
    name: 'Ylang Ylang Essential Oil',
    latin: 'Cananga odorata',
    category: 'Essential Oil',
    benefits: 'A lush, floral oil that lifts mood, softens tension and invites a little sensuality.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil']
  },
  {
    name: 'Bergamot Essential Oil',
    latin: 'Citrus bergamia',
    category: 'Essential Oil',
    benefits: 'A bright, citrus oil that brightens the mood without being overly stimulating \u2014 perfect for balance.',
    foundIn: ['rejuvenating-massage-oil', 'relax-massage-oil']
  },
  {
    name: 'Tea Tree Essential Oil',
    latin: 'Melaleuca alternifolia',
    category: 'Essential Oil',
    benefits: 'A clarifying oil with a fresh, medicinal aroma \u2014 traditionally used to purify skin and air.',
    foundIn: ['rejuvenating-massage-oil']
  },
  {
    name: 'Orange Essential Oil',
    latin: 'Citrus sinensis',
    category: 'Essential Oil',
    benefits: 'Sunshine in a bottle. Uplifting, cheerful and lightly cleansing \u2014 wonderful for mornings.',
    foundIn: ['rejuvenating-massage-oil']
  },
  {
    name: 'Marjoram Essential Oil',
    latin: 'Origanum majorana',
    category: 'Essential Oil',
    benefits: 'A warm, herbal oil traditionally used to soften stress and support restful evenings.',
    foundIn: ['relax-massage-oil']
  },
  {
    name: 'Benzoin Essential Oil',
    latin: 'Styrax benzoin',
    category: 'Essential Oil',
    benefits: 'A sweet, vanilla-like resin that grounds a blend and adds a soft, meditative aroma.',
    foundIn: ['relax-massage-oil']
  },

  // ---- Sweeteners & Extras ----
  {
    name: 'Raw Honey',
    category: 'Sweetener',
    benefits: 'A living sweetener with soothing, immune-supportive qualities \u2014 gentle on the throat and stomach.',
    foundIn: ['fireball-elixir', 'respiratory-support-elixir', 'sleep-syrup-w-cbn']
  },
  {
    name: 'Glycerin (Vegetable)',
    category: 'Base',
    benefits: 'A sweet, plant-derived liquid that carries herbal actives and is gentle on the stomach.',
    foundIn: ['a-okay-elixir', 'boost-elixir', 'respiratory-support-elixir', 'sleep-syrup-w-cbn']
  }
];

export const glossaryCategories = ['All', ...Array.from(new Set(glossary.map(g => g.category)))].sort();
